import { PlainButton } from "@jugl-web/ui-components/cross-platform";
import { Tooltip } from "@jugl-web/ui-kit/primitives";
import { cx } from "@jugl-web/utils";
import { isColorDark } from "@jugl-web/utils/color";
import { CSSProperties, forwardRef, useState } from "react";
import {
  ChatBotUIConversationMessage,
  ChatBotUIProduct,
  ChatBotUISendMessagePayload,
} from "../../../../types";
import { ChatBotMessageBody } from "./ChatBotMessageBody";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ChatBotDateSlotBooking } from "./components/ChatBotDateSlotBooking";
import { ChatBotInputsForm } from "./components/ChatBotInputsForm";
import { ChatBotProductList } from "./components/ChatBotProductList";
import {
  AVATAR_BUBBLE_OFFSET,
  AVATAR_SIZE,
  ChatMessageBubble,
} from "./components/ChatMessageBubble";
import { QuickRepliesList } from "./components/QuickRepliesList";

export interface ChatBotMessageComponentProps {
  message: ChatBotUIConversationMessage;
  isLast: boolean;
  userBubbleColor: string;
  onSendMessage?: (payload: ChatBotUISendMessagePayload) => void;
  onOpenProductDetailsDrawer?: (product: ChatBotUIProduct) => void;
  onRevise?: () => void;
}

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const ChatBotMessageComponent = forwardRef<
  HTMLDivElement,
  ChatBotMessageComponentProps
>(
  (
    {
      message,
      isLast,
      userBubbleColor,
      onSendMessage,
      onOpenProductDetailsDrawer,
      onRevise,
    },
    ref
  ) => {
    const [footerEndSlotElement, setFooterEndSlotElement] =
      useState<HTMLDivElement | null>(null);

    // Incoming means the message is from the bot / customer support
    // Outgoing means the message is from the widget consumer
    const isOutgoing = message.type === "outgoing";

    const quickReplies =
      message.payload?.type === "text" ? message.payload.quickReplies : [];

    const formattedTimestamp = formatTime(message.timestamp);

    const outsideBubblePaddingStyles: CSSProperties = {
      paddingRight: isOutgoing ? AVATAR_SIZE + AVATAR_BUBBLE_OFFSET : undefined,
      paddingLeft: isOutgoing ? undefined : AVATAR_SIZE + AVATAR_BUBBLE_OFFSET,
    };

    const isTextColorLight = isColorDark(userBubbleColor);

    return (
      <div
        ref={ref}
        className={cx("flex w-full flex-col", {
          "items-end": isOutgoing,
          "items-start": !isOutgoing,
        })}
      >
        <ChatMessageBubble
          isOutgoing={isOutgoing}
          userBubbleColor={userBubbleColor}
          senderName={message.senderName}
          senderImageUrl={message.senderImageUrl}
          title={formattedTimestamp}
        >
          <div
            className={cx(
              "font-secondary m-0 break-words text-sm leading-[1.5]",
              {
                "text-white": isOutgoing && isTextColorLight,
                "text-black": isOutgoing && !isTextColorLight,
                "text-dark": !isOutgoing,
              }
            )}
          >
            <ChatBotMessageBody
              text={message.payload.text}
              whiteLinks={isOutgoing && isTextColorLight}
            />
          </div>
          {!isOutgoing && isLast && quickReplies.length > 0 && (
            <QuickRepliesList
              replies={quickReplies}
              onSelect={(reply) =>
                onSendMessage?.({ type: "text", text: reply })
              }
            />
          )}
        </ChatMessageBubble>
        {/* Extra content only for the last (active) message */}
        {isLast && (
          <>
            {/* Inputs form */}
            {message.payload.type === "inputs" && (
              <ChatBotInputsForm
                inputs={message.payload.inputs}
                className="mt-2"
                style={{
                  ...outsideBubblePaddingStyles,
                  maxWidth: `min(calc(100% - ${
                    AVATAR_SIZE + AVATAR_BUBBLE_OFFSET
                  }px), 400px)`,
                }}
                onSubmit={(data) =>
                  onSendMessage?.({ type: "submit-form", data })
                }
              />
            )}
            {/* Products list */}
            {message.payload.type === "productList" && (
              <ChatBotProductList
                products={message.payload.products}
                navigationSlotElement={footerEndSlotElement}
                className="mt-2"
                onSelectProduct={(product) =>
                  onOpenProductDetailsDrawer?.(product)
                }
              />
            )}
            {message.payload.type === "dateSlotBooking" && (
              <ChatBotDateSlotBooking
                slots={message.payload.slots}
                className="mt-2"
                style={{
                  ...outsideBubblePaddingStyles,
                  maxWidth: `min(calc(100% - ${
                    AVATAR_SIZE + AVATAR_BUBBLE_OFFSET
                  }px), 400px)`,
                }}
              />
            )}
          </>
        )}
        {/* Message footer */}
        {(isLast || onRevise) && (
          <div
            className={cx(
              "mt-2 flex w-full items-center justify-between gap-2",
              isOutgoing && "flex-row-reverse"
            )}
          >
            {/* Footer start */}
            <div
              className="flex items-center gap-2"
              style={outsideBubblePaddingStyles}
            >
              {/* Timestamp */}
              {isLast && (
                <span className="font-secondary text-dark-600 text-xs leading-[1.4]">
                  {formattedTimestamp}
                </span>
              )}
              {isLast && onRevise && (
                <div className="bg-dark-100 mx-2 h-4 w-px shrink-0 self-center" />
              )}
              {onRevise && (
                <Tooltip
                  renderTrigger={({ props, ref: tooltipRef }) => (
                    <PlainButton
                      className="hover:bg-grey-200 text-dark-600 hover:text-dark-800 flex h-7 w-7 items-center justify-center rounded-md transition-colors"
                      onClick={onRevise}
                      {...props}
                      ref={tooltipRef}
                    >
                      <EditIcon />
                    </PlainButton>
                  )}
                >
                  Revise Answer
                </Tooltip>
              )}
            </div>
            {/* Footer end */}
            {isLast && <div ref={setFooterEndSlotElement} />}
          </div>
        )}
      </div>
    );
  }
);
